"use client";

import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import MainPageWrapper from "../../../../../../components/MainPageWrapper/MainPageWrapper";
import clsx from "clsx";
import { UserCircleIcon, Cog6ToothIcon, ServerStackIcon } from "@heroicons/react/24/outline";
import UserProfile from "../UserProfile/UserProfile";
import CompanyProfile from "../CompanyProfile/CompanyProfile";
import ServiceTitanIntegration from "../../../CompanyManagement/components/ServiceTitanIntegration/ServiceTitanIntegration";
import permissionsService from "../../../../../../services/permissionsService";
import { RootState } from "../../../../../../app/rootReducer";

const Settings: React.FC = () => {
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState("User Profile");

  // Handle default tab from navigation state
  useEffect(() => {
    const state = location.state as { defaultTab?: string } | null;
    if (state?.defaultTab === "servicetitan") {
      setCurrentTab("ServiceTitan");
    }
  }, [location]);
  const { hasRole } = permissionsService();

  // Get company profile to access UUID and ID
  const { companyProfile } = useSelector((state: RootState) => state.companyProfile);
  const { userAppSettings } = useSelector((state: RootState) => state.userAppSettings);

  // Check if user can manage company settings (super admin or owner/GM)
  const canManageCompany = hasRole("ROLE_SUPER_ADMIN") || hasRole("ROLE_OWNER_GM");

  const tabs = [
    { name: "User Profile", key: "userProfile", icon: UserCircleIcon },
  ];

  // Add Company Profile tab only for super admin or owner/GM
  if (canManageCompany) {
    tabs.push({ name: "Company Profile", key: "companyProfile", icon: Cog6ToothIcon });
  }

  // Add ServiceTitan tab for super admin/owner GM
  if (canManageCompany) {
    tabs.push({ name: "ServiceTitan", key: "serviceTitan", icon: ServerStackIcon });
  }

  /*{ name: "Security", key: "security", icon: LockClosedIcon },*/

  return (
    <MainPageWrapper error={null} loading={false} title="Settings">
      <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <div className="border-b border-gray-200">
            <nav aria-label="Tabs" className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  aria-current={currentTab === tab.name ? "page" : undefined}
                  className={clsx(
                    currentTab === tab.name
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                    "flex items-center whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium",
                  )}
                  key={tab.key}
                  onClick={() => setCurrentTab(tab.name)}
                >
                  <tab.icon aria-hidden="true" className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className={currentTab === "User Profile" ? "block" : "hidden"}>
          <UserProfile />
        </div>

        {canManageCompany && (
          <div className={currentTab === "Company Profile" ? "block" : "hidden"}>
            <CompanyProfile />
          </div>
        )}

        {canManageCompany && companyProfile && (
          <div className={currentTab === "ServiceTitan" ? "block" : "hidden"}>
            <ServiceTitanIntegration
              companyUuid={companyProfile.uuid}
              companyId={userAppSettings?.companyId}
            />
          </div>
        )}

        {/*{currentTab === "Security" && <Security />}*/}
      </div>
    </MainPageWrapper>
  );
};

export default Settings;
